import React from "react";
import { SketchPicker, type ColorResult } from "react-color";
import { useAppDispatch } from "@/state/hooks";
import { updateTagThunk } from "@/state/tags/thunks";

const PICKER_WIDTH = 220;
const PICKER_OFFSET = 8;

export type TagColorPickerState = {
    tag: { id: string | number; color?: string };
    anchorRect: DOMRect;
};

interface TagColorMenuItemProps {
    tag: { id: string | number; color?: string };
    id: string;
    onOpen: (state: TagColorPickerState) => void;
}

export function TagColorMenuItem({ tag, id, onOpen }: TagColorMenuItemProps) {
    return (
        <button
            type="button"
            className="dropdown-item gs-tag-color-trigger"
            id={id}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpen({
                    tag,
                    anchorRect: (e.currentTarget as HTMLElement).getBoundingClientRect(),
                });
            }}
        >
            Change Color
        </button>
    );
}

interface TagColorPickerFloatingProps {
    picker: TagColorPickerState;
    onClose: () => void;
}

export function TagColorPickerFloating({ picker, onClose }: TagColorPickerFloatingProps) {
    const dispatch = useAppDispatch();
    const panelRef = React.useRef<HTMLDivElement>(null);
    const [pickerColor, setPickerColor] = React.useState(picker.tag.color || "#6C57D1");
    const [position, setPosition] = React.useState(() => getPickerPosition(picker.anchorRect));

    React.useEffect(() => {
        setPickerColor(picker.tag.color || "#6C57D1");
        setPosition(getPickerPosition(picker.anchorRect));
    }, [picker]);

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        const handleScroll = () => {
            onClose();
        };

        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("scroll", handleScroll, true);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [onClose]);

    const handleChange = (color: ColorResult) => {
        setPickerColor(color.hex);
    };

    const handleChangeComplete = async (color: ColorResult) => {
        const newColor = color.hex.toUpperCase();
        try {
            await dispatch(
                updateTagThunk({ id: picker.tag.id, tagData: { color: newColor } })
            ).unwrap();
        } catch (err: unknown) {
            console.error("Error updating tag color:", err);
            setPickerColor(picker.tag.color || "#6C57D1");
        }
    };

    const handleBackdropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (panelRef.current?.contains(event.target as Node)) {
            return;
        }
        onClose();
    };

    return (
        <div
            className="gs-tag-color-picker-backdrop"
            onPointerDown={handleBackdropPointerDown}
            aria-hidden
        >
            <div
                ref={panelRef}
                className="gs-tag-color-picker-floating gs-tag-color-picker-surface"
                style={{ top: position.top, left: position.left }}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <SketchPicker
                    color={pickerColor}
                    onChange={handleChange}
                    onChangeComplete={handleChangeComplete}
                />
            </div>
        </div>
    );
}

function getPickerPosition(anchorRect: DOMRect) {
    const viewportPadding = 12;
    let left = anchorRect.left - PICKER_WIDTH - PICKER_OFFSET;
    let top = anchorRect.top;

    if (left < viewportPadding) {
        left = anchorRect.right + PICKER_OFFSET;
    }

    const maxTop = window.innerHeight - 320 - viewportPadding;
    top = Math.max(viewportPadding, Math.min(top, maxTop));

    return { top, left };
}

export default TagColorMenuItem;
